import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { locField } from "@/lib/i18nField";
import { X } from "lucide-react";

const TYPES = ["discussion", "question", "photo", "recommendation", "carpool"];

export default function CreatePostDialog({ open, onClose, categories = [], defaultCategory = "", link = {}, onCreated }) {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ category_key: defaultCategory, type: "discussion", title: "", content: "", original_language: "" });
  const [hp, setHp] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { if (open) setForm((f) => ({ ...f, category_key: defaultCategory || f.category_key })); }, [open, defaultCategory]);

  if (!open) return null;
  const postable = categories.filter((c) => c.members_can_post);

  const submit = async () => {
    if (hp) return; // bot
    if (!form.category_key) { toast.error(t("com_select_category")); return; }
    if (form.content.trim().length < 2) return;
    setBusy(true);
    try {
      const payload = { ...form, ...link, title: form.title.trim(), content: form.content.trim() };
      if (!payload.original_language) delete payload.original_language;
      const { data } = await api.post("/community/posts", payload);
      toast.success(t("com_saved"));
      setForm({ category_key: defaultCategory, type: "discussion", title: "", content: "", original_language: "" });
      onClose?.();
      onCreated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("com_error")); } finally { setBusy(false); }
  };

  const field = "w-full bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";
  return (
    <div className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-lg bg-[#121418] border border-[#272A34] rounded-t-2xl sm:rounded-xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-post-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-amber-100">{t("com_create_post")}</h3>
          <button onClick={onClose} data-testid="create-post-close" className="text-stone-400 hover:text-stone-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="create-post-category" value={form.category_key} onChange={(e) => setForm({ ...form, category_key: e.target.value })} className={field}>
              <option value="">{t("com_select_category")}</option>
              {postable.map((c) => <option key={c.key} value={c.key}>{locField(c.translations?.name, lang)}</option>)}
            </select>
            <select data-testid="create-post-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={field}>
              {TYPES.map((ty) => <option key={ty} value={ty}>{t(`com_type_${ty}`)}</option>)}
            </select>
          </div>
          <input data-testid="create-post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("com_title_ph")} className={field} />
          <textarea data-testid="create-post-content" rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder={t("com_content_ph")} className={`${field} resize-none`} />
          <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1">{t("com_post_lang")}</label>
            <select data-testid="create-post-lang" value={form.original_language} onChange={(e) => setForm({ ...form, original_language: e.target.value })} className={field}>
              <option value="">Auto</option>
              <option value="fr">Français</option><option value="de">Deutsch</option>
              <option value="it">Italiano</option><option value="en">English</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-stone-300 border border-[#272A34]">{t("com_cancel")}</button>
            <button data-testid="create-post-submit" onClick={submit} disabled={busy || form.content.trim().length < 2 || !form.category_key} className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-2 rounded-md text-sm disabled:opacity-50">{t("com_publish")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
